import React, { useState, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { formatTime } from '../utils/timeUtils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ProblemRecord {
  id: string;
  problemNumber: number;
  timeSpent: number;
  passed: boolean;
  timestamp: number;
  aiSolution?: string;
  date: string;
  errorType?: string;
  errorMade?: string;
  difficulty?: string;
  notes?: string;
  image?: string;
}

interface QuantErrorRecord {
  id: string;
  problemNumber: number;
  errorType: string;
  errorMade: string;
  difficulty: string;
  notes?: string;
  timestamp: number;
  date: string;
  image?: string;
  timeSpent?: number;
}

export interface ProblemDetailProps {
  mode: 'review' | 'history';
  problems: ProblemRecord[];
  initialIndex: number;
  errors: QuantErrorRecord[];
  onBack: () => void;
  getImageSrc: (image?: string | null) => string | undefined;
  onSolveWithAI: (problemId: string, image: string) => void;
  solvingId: string | null;
  showAILimitBanner: boolean;
  onDismissLimitBanner: () => void;
}

// ── Chip helpers ───────────────────────────────────────────────────────────────

const chip = (
  label: string,
  bg: string,
  color: string,
  border?: string
): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '5px 11px',
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 500,
  backgroundColor: bg,
  color,
  border: border ?? 'none',
  whiteSpace: 'nowrap' as const,
  flexShrink: 0,
});

const difficultyChip = (difficulty: string): React.CSSProperties => {
  if (difficulty === 'Easy') return chip('Easy', '#EAF3DE', '#3B6D11');
  if (difficulty === 'Medium') return chip('Medium', '#FAEEDA', '#854F0B');
  return chip('Hard', '#FCEBEB', '#A32D2D');
};

const EYEBROW: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: '#478475',
  marginBottom: 8,
};

const DIVIDER: React.CSSProperties = {
  height: 1,
  backgroundColor: '#f0f4f2',
  margin: '0 -20px',
};

// ── Component ─────────────────────────────────────────────────────────────────

const ProblemDetail: React.FC<ProblemDetailProps> = ({
  mode,
  problems,
  initialIndex,
  errors,
  onBack,
  getImageSrc,
  onSolveWithAI,
  solvingId,
  showAILimitBanner,
  onDismissLimitBanner,
}) => {
  const { isPro, isTrial } = useSubscription();
  const navigate = useNavigate();

  const [currentIndex, setCurrentIndex] = useState(Math.max(0, Math.min(initialIndex, problems.length - 1)));
  const [slideStyle, setSlideStyle] = useState<React.CSSProperties>({ transform: 'translateX(0)', transition: 'none' });
  const [navigating, setNavigating] = useState(false);

  const touchStartX = useRef<number | null>(null);

  const problem = problems[currentIndex];

  const getError = (problemNumber: number) =>
    errors.find(e => e.problemNumber === problemNumber) ?? null;

  const goTo = useCallback((nextIndex: number, dir: 'left' | 'right') => {
    if (navigating) return;
    if (nextIndex < 0 || nextIndex >= problems.length) return;
    setNavigating(true);

    // Exit current card
    setSlideStyle({
      transform: dir === 'left' ? 'translateX(-100%)' : 'translateX(100%)',
      transition: 'transform 220ms cubic-bezier(0.4, 0, 0.2, 1)',
    });

    setTimeout(() => {
      setCurrentIndex(nextIndex);
      // Position incoming card off-screen (no transition)
      setSlideStyle({
        transform: dir === 'left' ? 'translateX(100%)' : 'translateX(-100%)',
        transition: 'none',
      });
      // On next paint, slide it in
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSlideStyle({
            transform: 'translateX(0)',
            transition: 'transform 220ms cubic-bezier(0.4, 0, 0.2, 1)',
          });
          setTimeout(() => setNavigating(false), 230);
        });
      });
    }, 230);
  }, [navigating, problems.length]);

  const goNext = () => goTo(currentIndex + 1, 'left');
  const goPrev = () => goTo(currentIndex - 1, 'right');

  // Touch handlers
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) >= 40) {
      delta > 0 ? goNext() : goPrev();
    }
    touchStartX.current = null;
  };

  if (!problem) return null;

  const error = getError(problem.problemNumber);
  const imageSrc = getImageSrc(problem.image ?? error?.image);
  const isLoading = solvingId === problem.id;
  const solution = problem.aiSolution;

  // Show error analysis section if there's any error data
  const hasErrorAnalysis = !problem.passed && (
    (problem.errorType && problem.errorType !== 'Not specified') ||
    (problem.errorMade && problem.errorMade !== 'Not specified') ||
    problem.difficulty ||
    (error?.errorType && error.errorType !== 'Not specified') ||
    (error?.errorMade && error.errorMade !== 'Not specified') ||
    error?.difficulty
  );

  const displayErrorType = problem.errorType || error?.errorType;
  const displayErrorMade = problem.errorMade || error?.errorMade;
  const displayDifficulty = problem.difficulty || error?.difficulty;
  const displayNotes = problem.notes || error?.notes;

  const progress = problems.length > 1 ? currentIndex / (problems.length - 1) : 1;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 'calc(100vh - 60px)',
      backgroundColor: 'var(--color-background)',
      overflow: 'hidden',
    }}>

      {/* ── Trial limit banner ── */}
      {showAILimitBanner && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          zIndex: 10000, backgroundColor: '#fff',
          border: '1.5px solid #1F4C40', borderRadius: 16,
          padding: '14px 18px', boxShadow: '0 8px 24px rgba(31,76,64,0.2)',
          maxWidth: 320, width: 'calc(100% - 32px)',
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1F4C40', marginBottom: 4 }}>
            Trial limit reached
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 12, lineHeight: 1.4 }}>
            Trial members get 1 AI explanation per day. Upgrade to Pro for unlimited solves.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => { onDismissLimitBanner(); navigate('/upgrade'); }}
              style={{
                flex: 1, padding: '9px 0', borderRadius: 10,
                backgroundColor: '#1F4C40', color: '#fff',
                border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}
            >
              Upgrade to Pro
            </button>
            <button
              onClick={onDismissLimitBanner}
              style={{
                padding: '9px 14px', borderRadius: 10,
                backgroundColor: 'transparent', color: 'var(--color-text-muted)',
                border: '1px solid var(--color-border)', fontSize: 13, cursor: 'pointer',
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* ── Top bar ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '10px 16px',
        gap: 8,
        flexShrink: 0,
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            padding: '6px 8px 6px 0',
            cursor: 'pointer',
            color: 'var(--color-primary)',
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1,
            flexShrink: 0,
          }}
          aria-label="Back"
        >
          ‹
        </button>

        <span style={{
          flex: 1,
          fontSize: 17,
          fontWeight: 700,
          color: '#1F4C40',
          textAlign: 'center',
        }}>
          {mode === 'review' ? 'Review Problems' : 'Problem History'}
        </span>

        {mode === 'review' ? (
          <button
            onClick={onBack}
            style={{
              padding: '6px 14px',
              borderRadius: 999,
              backgroundColor: 'var(--color-text-muted)',
              color: '#fff',
              border: 'none',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            Exit
          </button>
        ) : (
          <div style={{ width: 52 }} /> // spacer to balance the back button
        )}
      </div>

      {/* ── Progress bar ── */}
      <div style={{ padding: '0 16px 10px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            flex: 1,
            height: 4,
            borderRadius: 999,
            backgroundColor: '#e4ede9',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${progress * 100}%`,
              backgroundColor: '#1F4C40',
              borderRadius: 999,
              transition: 'width 220ms ease',
            }} />
          </div>
          <span style={{ fontSize: 12, color: '#478475', fontWeight: 600, flexShrink: 0 }}>
            {currentIndex + 1} of {problems.length}
          </span>
        </div>
      </div>

      {/* ── Animated card area ── */}
      <div style={{
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
        padding: '0 12px',
      }}>
        <div
          style={{
            height: '100%',
            ...slideStyle,
          }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Card */}
          <div style={{
            height: '100%',
            backgroundColor: '#fff',
            borderRadius: 20,
            border: '1px solid #e4ede9',
            boxShadow: '0 4px 16px rgba(31,76,64,0.08)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>

            {/* Scrollable content area */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 0' }}>

              {/* Problem section */}
              <div style={{ marginBottom: 16 }}>
                <p style={EYEBROW}>Problem</p>
                <h2 style={{ fontSize: 26, fontWeight: 800, color: '#1F4C40', margin: 0, lineHeight: 1.2 }}>
                  Problem #{problem.problemNumber}
                </h2>
              </div>

              <div style={DIVIDER} />

              {/* Result section */}
              <div style={{ padding: '14px 0' }}>
                <p style={EYEBROW}>Result</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                  <span style={problem.passed
                    ? chip('Pass', '#EAF3DE', '#3B6D11')
                    : chip('Fail', '#FCEBEB', '#A32D2D')
                  }>
                    {problem.passed ? '✓ Passed' : '✕ Failed'}
                  </span>
                  <span style={chip('time', '#f0f4f2', '#3a5c52', '0.5px solid #c8ddd8')}>
                    ⏱ {formatTime(problem.timeSpent)}
                  </span>
                  <span style={chip('date', '#f0f4f2', '#3a5c52', '0.5px solid #c8ddd8')}>
                    {new Date(problem.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              </div>

              {/* Error analysis section */}
              {hasErrorAnalysis && (
                <>
                  <div style={DIVIDER} />
                  <div style={{ padding: '14px 0' }}>
                    <p style={EYEBROW}>Error Analysis</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {displayErrorType && displayErrorType !== 'Not specified' && (
                        <span style={chip(displayErrorType, '#1F4C40', '#E6F1EC')}>
                          {displayErrorType}
                        </span>
                      )}
                      {displayErrorMade && displayErrorMade !== 'Not specified' && (
                        <span style={chip(displayErrorMade, '#E1F5EE', '#085041', '0.5px solid #9FE1CB')}>
                          {displayErrorMade}
                        </span>
                      )}
                      {displayDifficulty && displayDifficulty !== 'Not specified' && (
                        <span style={difficultyChip(displayDifficulty)}>
                          {displayDifficulty}
                        </span>
                      )}
                    </div>
                    {displayNotes && displayNotes.trim() && (
                      <p style={{ fontSize: 13, color: '#3a5c52', marginTop: 10, marginBottom: 0, lineHeight: 1.5, fontStyle: 'italic' }}>
                        {displayNotes}
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Photo section */}
              {imageSrc && (
                <>
                  <div style={DIVIDER} />
                  <div style={{ padding: '14px 0 0' }}>
                    <p style={EYEBROW}>Problem Photo</p>
                    <img
                      src={imageSrc}
                      alt="Problem"
                      style={{
                        width: '100%',
                        borderRadius: 12,
                        display: 'block',
                        objectFit: 'contain',
                      }}
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  </div>
                </>
              )}

              {/* AI Solution (when solved) */}
              {solution && (
                <>
                  <div style={{ ...DIVIDER, marginTop: 14 }} />
                  <div style={{ padding: '14px 0' }}>
                    <p style={EYEBROW}>AI Solution</p>
                    <div style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--color-text)' }}>
                      <ReactMarkdown>{solution}</ReactMarkdown>
                    </div>
                  </div>
                </>
              )}

              {/* bottom padding so content isn't hidden behind pinned button */}
              <div style={{ height: 80 }} />
            </div>

            {/* Pinned AI button at bottom of card */}
            {imageSrc && !solution && (
              <div style={{ padding: '12px 16px 16px', borderTop: '1px solid #f0f4f2', backgroundColor: '#fff' }}>
                {showAILimitBanner ? (
                  <button
                    onClick={() => { onDismissLimitBanner(); navigate('/upgrade'); }}
                    style={{
                      width: '100%', padding: '12px 0', borderRadius: 999,
                      backgroundColor: '#1F4C40', color: '#fff',
                      border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                    }}
                  >
                    Upgrade to Pro — unlimited AI solves
                  </button>
                ) : (
                  <button
                    disabled={isLoading}
                    onClick={() => {
                      if (!isPro) { navigate('/upgrade'); return; }
                      if (!isLoading && imageSrc) onSolveWithAI(problem.id, imageSrc);
                    }}
                    style={{
                      width: '100%', padding: '12px 0', borderRadius: 999,
                      backgroundColor: '#1F4C40',
                      color: '#fff',
                      border: 'none', fontWeight: 700, fontSize: 14,
                      cursor: isLoading ? 'default' : 'pointer',
                      opacity: isLoading ? 0.7 : 1,
                    }}
                  >
                    {isLoading
                      ? '⏳ Analyzing...'
                      : isPro
                        ? (isTrial ? '✦ Solve with AI (1/day)' : '✦ Solve with AI')
                        : '✦ Solve with AI — Upgrade to Pro'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Bottom nav ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px 16px',
        flexShrink: 0,
        gap: 12,
      }}>
        <button
          onClick={goPrev}
          disabled={currentIndex === 0 || navigating}
          style={{
            padding: '11px 22px',
            borderRadius: 999,
            backgroundColor: currentIndex === 0 ? 'var(--color-surface-muted)' : '#1F4C40',
            color: currentIndex === 0 ? 'var(--color-text-muted)' : '#fff',
            border: 'none',
            fontWeight: 700,
            fontSize: 14,
            cursor: currentIndex === 0 ? 'default' : 'pointer',
            opacity: currentIndex === 0 ? 0.45 : 1,
          }}
        >
          ← Previous
        </button>

        <span style={{ fontSize: 12, color: '#478475', textAlign: 'center', lineHeight: 1.3 }}>
          swipe left / right
        </span>

        <button
          onClick={goNext}
          disabled={currentIndex === problems.length - 1 || navigating}
          style={{
            padding: '11px 22px',
            borderRadius: 999,
            backgroundColor: currentIndex === problems.length - 1 ? 'var(--color-surface-muted)' : '#1F4C40',
            color: currentIndex === problems.length - 1 ? 'var(--color-text-muted)' : '#fff',
            border: 'none',
            fontWeight: 700,
            fontSize: 14,
            cursor: currentIndex === problems.length - 1 ? 'default' : 'pointer',
            opacity: currentIndex === problems.length - 1 ? 0.45 : 1,
          }}
        >
          Next →
        </button>
      </div>

    </div>
  );
};

export default ProblemDetail;
